import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  FormControlLabel,
  Typography,
  Box,
  Pagination,
  Divider,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation, useNavigate } from "react-router-dom";

const theme = createTheme({
  typography: {
    fontFamily: "Manrope, Arial, sans-serif",
    fontSize: 13,
  },
});

const ImportTestCases = ({
  almConfig,
  emptyParent,
  testCasesLoading,
  setSelectedParent,
  testCasesList,
  setTestCasesList,
  selectedTestCases,
  setSelectedTestCases,
  selectedProjectName,
  setImportPageOpen,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const totalItems = testCasesList.length;
  const itemsPerPage = 7;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const [currentPage, setCurrentPage] = useState(1);

  // --------------------------
  // Single Selection Only
  // --------------------------
  const handleSelect = (testCase) => {
    setSelectedTestCases([testCase]); // Always keep only ONE selected
  };

  const isSelected = (testCase) =>
    selectedTestCases.length > 0 &&
    selectedTestCases[0].id === testCase.id;

  const handlePageChange = (_event, page) => {
    setCurrentPage(page);
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = testCasesList.slice(startIndex, startIndex + itemsPerPage);

  const handleClose = () => {
    emptyParent();
    setTestCasesList([]);
    setSelectedTestCases([]);
  };

  const handleNavigate = () => {
  console.log("Selected Test Cases:", selectedTestCases);

  if (selectedTestCases.length === 0) return;

  const tc = selectedTestCases[0]; // Single selected

  const mappedTestCase = {
    "Test Case ID": tc.id || "",
    "Test Case Title": tc.Title || "",
    "Module Name": "",
    "Test Objective": tc.Description || "",
    "Preconditions": "",
    "Test Steps": tc.Steps || "",
    "Expected Result": "",
    "Post-conditions": "",
    "Test Data (if applicable)": "",
    "Priority": tc.Priority || "",
    "Type": "",
    "Remarks": "",
  };

  navigate(location.pathname, {
    state: {
      ...location.state,
      testCaseData: mappedTestCase,   
    },
  });

  setImportPageOpen(false);
};


  const handleCancel = () => {
    setImportPageOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open
        sx={{
          "& .MuiDialog-paper": {
            width: "600px",
            height: "600px",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{ mb: -0.5, display: "flex", justifyContent: "space-between" }}
        >
          <Box display="flex" alignItems="center" gap={1} sx={{ ml: -1 }}>
            <ArrowBackIosNewIcon
              sx={{ fontSize: 11, color: "#000", cursor: "pointer" }}
              onClick={handleClose}
            />
            Import Test Cases
          </Box>
          <IconButton onClick={handleCancel}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            <strong>{selectedProjectName}</strong>
          </Typography>

          <Typography variant="body2" sx={{ opacity: 0.6, mb: 1 }}>
            Select one test case to import
          </Typography>

          <Box
            sx={{
              overflowY: "visible",
              maxHeight: "400px",
              backgroundColor: "#F0F8FF",
              border: "1px dashed #49A3FF",
              borderRadius: "7px",
              px: 3,
              py: 1,
              ml: 1,
            }}
          >
            {testCasesList.length > 0 ? (
              <Box sx={{ ml: 1 }}>
                {currentItems.map((item, idx) => (
                  <Box key={startIndex + idx} sx={{ mb: 0.5, ml: 2 }}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={isSelected(item)}
                          onChange={() => handleSelect(item)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">{item.Title}</Typography>
                      }
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "300px",
                }}
              >
                {testCasesLoading ? (
                  <CircularProgress />
                ) : (
                  <Typography>No test cases found</Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mt: 2,
            px: 2,
            justifyContent: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              flexShrink: 0,
              pl: 3,
              position: "absolute",
              left: 0,
              mb: 1,
            }}
          >
            {currentItems.length}/{testCasesList.length} Items
          </Typography>

          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              sx={{
                "& .MuiPaginationItem-root": { color: "black" },
                "& .MuiPaginationItem-root.Mui-selected": {
                  backgroundColor: "lightgray",
                  color: "black",
                },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        <DialogActions sx={{ justifyContent: "flex-end", px: 3 }}>
          <Button
            variant="contained"
            onClick={handleNavigate}
            disabled={selectedTestCases.length === 0}
            sx={{ backgroundColor: "#49A3FF", textTransform: "none" }}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default ImportTestCases;